/**
 * Every kind of point of interest the model knows, grouped the way the model
 * groups them.
 *
 * These are not metrics. A metric colours the grid and there is exactly one of
 * it — asking for two at once has no picture. Points of interest are markers on
 * top, and several at once is the normal case: a site is judged by charging
 * points *and* schools *and* the pharmacy, not by one of them. So they add up
 * where a metric replaces.
 *
 * The classes are the model's `PoI` subclasses, the group is the `poiGroup`
 * each of them sets, the kind's label is its `EntityType.label`. A grouping
 * invented here alongside the model's own would be two answers to one question,
 * and the first rename would put them out of step.
 *
 * The group names and their order come from `PoIGroup` and `PoIGroupI18N`, so
 * nothing about this vocabulary is stated twice.
 *
 * The group is read by instantiating each class once: it is assigned in the
 * constructor, not declared statically, so there is no other way to ask.
 */

import {
    AdvertisementColumn,
    Airport,
    Billboard,
    BusStop,
    CareFacility,
    CellularTower,
    ChargingStation,
    ChildCareFacility,
    Church,
    DataCenter,
    Doctor,
    FireStation,
    FreewayMaintenanceOffice,
    Hairdresser,
    Supermarket,
    Hospital,
    Hotel,
    MotorwayLink,
    ParcelStation,
    ParkingLot,
    Pharmacy,
    PoIGroup,
    PoIGroupI18N,
    PoIType,
    PoliceStation,
    PollutionData,
    PowerUnit,
    Restaurant,
    School,
    TrainStation,
    University,
} from '@the-real-insight/tri-model';

export interface PoiType {
    key: string;
    entityType: any;
    group: string;
    /** The `PoIType` the map keys its pin and its border colour on. */
    poiIcon: string;
}

export interface PoiGroupEntry {
    key: string;
    types: PoiType[];
}

/**
 * The marker type of a kind of place.
 *
 * The map has a pin per kind — a pharmacy cross, a bus stop, a church — and picks
 * it from `PoIType`, which is the same name as the class with a small first
 * letter. It reads the border colour off the same value, so getting this wrong
 * shows up twice: a generic pin in a generic black.
 *
 * A grouping was the wrong level for this. It is right for the *list*, where
 * "Gesundheit" is what a reader scans for, and wrong for the *map*, where the
 * whole point of a marker is that a doctor does not look like a hospital.
 *
 * Not every kind has a pin — `MotorwayLink` and `DataCenter` have none — and
 * those fall back to the map's own default rather than being named something they
 * are not.
 */
const poiTypeOf = (className: string): string => {
    const name = className.charAt(0).toLowerCase() + className.slice(1);

    return (PoIType as any)[name] ?? name;
};

const CLASSES: [string, any][] = [
    ['ChargingStation', ChargingStation],
    ['BusStop', BusStop],
    ['TrainStation', TrainStation],
    ['Airport', Airport],
    ['ParkingLot', ParkingLot],
    ['MotorwayLink', MotorwayLink],
    ['FreewayMaintenanceOffice', FreewayMaintenanceOffice],
    ['ChildCareFacility', ChildCareFacility],
    ['School', School],
    ['University', University],
    ['Doctor', Doctor],
    ['Pharmacy', Pharmacy],
    ['Hospital', Hospital],
    ['CareFacility', CareFacility],
    ['FireStation', FireStation],
    ['PoliceStation', PoliceStation],
    ['Church', Church],
    ['Restaurant', Restaurant],
    ['Hotel', Hotel],
    ['Hairdresser', Hairdresser],
    ['Supermarket', Supermarket],
    ['ParcelStation', ParcelStation],
    ['PowerUnit', PowerUnit],
    ['CellularTower', CellularTower],
    ['DataCenter', DataCenter],
    ['AdvertisementColumn', AdvertisementColumn],
    ['Billboard', Billboard],
    ['PollutionData', PollutionData],
];

/** The group a class belongs to, asked of an instance because that is where it lives. */
const groupOf = (entityType: any): string => {
    try {
        return new entityType({}).poiGroup ?? PoIGroup.infrastructure;
    } catch {
        return PoIGroup.infrastructure;
    }
};

const ALL_TYPES: PoiType[] = CLASSES.map(([key, entityType]) => ({
    key,
    entityType,
    group: groupOf(entityType),
    poiIcon: poiTypeOf(key),
}));

/** Groups in the model's own order, each with the classes that declare it. */
export const POI_GROUPS: PoiGroupEntry[] = Object.values(PoIGroup)
    .map((group) => ({
        key: group as string,
        types: ALL_TYPES.filter((entry) => entry.group === group),
    }))
    .filter((group) => group.types.length > 0);

/** Flat lookup by key, for turning a selection back into something queryable. */
export const POI_TYPES: Record<string, PoiType> = ALL_TYPES.reduce(
    (map, entry) => {
        map[entry.key] = entry;
        return map;
    },
    {} as Record<string, PoiType>,
);

const language = (value: string): 'de' | 'en' => (value.startsWith('de') ? 'de' : 'en');

/**
 * The label the model gives this kind, in the reader's language.
 *
 * Falling back to the class name means a subclass added without a label still
 * shows something recognisable rather than an empty row.
 */
export const poiTypeLabel = (entry: PoiType, locale: string): string => {
    const label = entry.entityType?.type?.label;
    const key = language(locale);

    return label?.[key] ?? label?.en ?? entry.key;
};

export const poiGroupLabel = (group: string, locale: string): string =>
    (PoIGroupI18N as any)?.[language(locale)]?.[group] ?? group;
